import { DocumentJobError, FlowType, UploadSource } from '@domain/types';
import { NotificationActions } from '@store/notificationWindow/notificationWindow.action';

/**
 * The notification window builds its whole UI from payloads that arrive over the IPC relay
 * (`bridge:util:sendTo`). The main process now restricts which channels can be relayed, but it
 * cannot know what a given payload should look like — so the shapes are checked here, before
 * anything reaches the store. A forged `addSuccess` would otherwise show the practitioner a
 * "document sent" confirmation for a delivery that never happened.
 */

const FLOW_TYPES: readonly FlowType[] = ['integration', 'sending'];
const UPLOAD_SOURCES: readonly UploadSource[] = ['opening', 'watcher', 'button', 'drop'];
const DOCUMENT_JOB_ERRORS = new Set<string>(Object.values(DocumentJobError));

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export const isFlowType = (value: unknown): value is FlowType =>
  FLOW_TYPES.includes(value as FlowType);

const isJobPayload = (payload: unknown): payload is Record<string, unknown> =>
  isRecord(payload) &&
  typeof payload.id === 'string' &&
  typeof payload.fileName === 'string' &&
  isFlowType(payload.flowType);

const isPendingPayload = (payload: unknown): boolean =>
  isRecord(payload) && typeof payload.count === 'number' && isFlowType(payload.flowType);

const isSuccessPayload = (payload: unknown): boolean => isJobPayload(payload);

const isErrorPayload = (payload: unknown): boolean =>
  isJobPayload(payload) &&
  UPLOAD_SOURCES.includes(payload.uploadSource as UploadSource) &&
  Array.isArray(payload.errors) &&
  payload.errors.every((error) => typeof error === 'string' && DOCUMENT_JOB_ERRORS.has(error));

const PAYLOAD_VALIDATORS: Record<string, (payload: unknown) => boolean> = {
  [NotificationActions.addPending.type]: isPendingPayload,
  [NotificationActions.addSuccess.type]: isSuccessPayload,
  [NotificationActions.addError.type]: isErrorPayload,
};

/** Unknown action types are rejected: only the three wired above may reach the store. */
export const isValidNotificationPayload = (type: string, payload: unknown): boolean =>
  PAYLOAD_VALIDATORS[type]?.(payload) ?? false;

/**
 * The main process stamps the real sender id into the NOTIFICATION_LOADED payload
 * (see `util.handlers.ts`), so a renderer cannot forge it. `event.senderId` is kept only as a
 * fallback for the legacy desktop shell, which exposes the raw ipcRenderer and still populates
 * that field — Electron 42 removed it along with `ipcRenderer.sendTo`.
 */
export const resolveSenderId = (event: unknown, payload: unknown): number | undefined => {
  const stamped = isRecord(payload) ? payload.senderId : undefined;
  if (Number.isInteger(stamped)) {
    return stamped as number;
  }

  const legacy = isRecord(event) ? event.senderId : undefined;
  return Number.isInteger(legacy) ? (legacy as number) : undefined;
};
